import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import type { CustomCellEditorProps } from 'ag-grid-react'

const INPUT_EDITOR_LOCALE_KEY = 'ariaInputEditor'
const INPUT_EDITOR_FALLBACK_LABEL = 'Input Editor'

type LargeTextEditorProps = CustomCellEditorProps & {
  cols?: number
  maxLength?: number
  rows?: number
}

const DELETE_START_KEYS = new Set(['Backspace', 'Delete'])
const NAVIGATION_KEYS = new Set([
  'ArrowLeft',
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
])

const toEditorString = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

const resolveStartValue = ({
  cellStartedEdit,
  eventKey,
  value,
}: LargeTextEditorProps): string => {
  if (!cellStartedEdit) return toEditorString(value)
  if (typeof eventKey === 'string') {
    if (DELETE_START_KEYS.has(eventKey)) return ''
    if (eventKey.length === 1) return eventKey
  }
  return toEditorString(value)
}

const resolveInputEditorLabel = (props: LargeTextEditorProps): string => {
  const { api } = props
  const getLocaleText = api.getGridOption('getLocaleText')
  if (typeof getLocaleText === 'function') {
    return getLocaleText({
      api,
      context: props.context,
      key: INPUT_EDITOR_LOCALE_KEY,
      defaultValue: INPUT_EDITOR_FALLBACK_LABEL,
      variableValues: [],
    })
  }

  const localeText = api.getGridOption('localeText')
  return localeText?.[INPUT_EDITOR_LOCALE_KEY] ?? INPUT_EDITOR_FALLBACK_LABEL
}

export function LargeTextEditor(props: LargeTextEditorProps) {
  const {
    cols = 50,
    maxLength = 200,
    onValueChange,
    rows = 5,
    cellStartedEdit,
    eventKey,
  } = props
  const [editorValue, setEditorValue] = useState(() => resolveStartValue(props))
  const inputEditorLabel = resolveInputEditorLabel(props)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    onValueChange(editorValue)
    // Seed AG Grid's editor proxy with keyboard-start values such as Backspace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.focus()
    if (
      cellStartedEdit &&
      !(typeof eventKey === 'string' && eventKey.length === 1)
    ) {
      textarea.select()
    }
  }, [cellStartedEdit, eventKey])

  const handleChange = (nextValue: string) => {
    setEditorValue(nextValue)
    onValueChange(nextValue)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      NAVIGATION_KEYS.has(event.key) ||
      (event.shiftKey && event.key === 'Enter')
    ) {
      event.stopPropagation()
    }
  }

  return (
    <div className="ag-large-text">
      <textarea
        ref={textareaRef}
        aria-label={inputEditorLabel}
        className="ag-input-field-input ag-text-area-input ag-large-text-input"
        cols={cols}
        maxLength={maxLength}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleKeyDown}
        rows={rows}
        title=""
        value={editorValue}
      />
    </div>
  )
}
